import React, { useState, useEffect, useMemo } from 'react';
import { io } from 'socket.io-client';
import { Container, Row, Col, Button, Badge, Spinner, Modal, Form } from 'react-bootstrap';
import { FaEye, FaEdit, FaTrash, FaUser, FaEnvelope, FaUniversity } from 'react-icons/fa';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Navigation from '../../Components/Navigation/Navigation';
import { API_BASE_URL } from '../../config/config';
import { ERROR_MESSAGES, SUCCESS_MESSAGES, APP_CONFIG } from '../../config/config';
import './ListAcademicians.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

function ListAcademicians() {
  const [user, setUser] = useState(null);
  const [academicians, setAcademicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addItem, setAddItem] = useState({
    ad_soyad:'',
    email:'',
    university:'',
    faculty:'',
    department:'',
    major:'',
    region:1,
    springerUrls:'',
    springerStartListUrl:'https://link.springer.com/journal/10462/articles'
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [toast, setToast] = useState(null); // { text, type: 'success' | 'error' | 'info' }
  const [scrapeProgress, setScrapeProgress] = useState(null); // { requestId, phase, message, current, total, ... }
  const [scrapeLogs, setScrapeLogs] = useState([]); // recent lines for visibility
  // Client-side sıralama/filtreleme istemiyoruz; tüm mantık API tarafında

  useEffect(() => {
    // localStorage'dan kullanıcı bilgilerini al
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      window.location.href = '/';
    }
    
    // Akademisyenleri yükle (API)
    loadAcademicians();
  }, []);

  // Global küçük toast bildirimi helper'ı
  const showToast = (text, type = 'success') => {
    console.log('🔔 Toast gösteriliyor:', text, type);
    setToast({ text, type });
  };

  // Toast'ı otomatik gizle
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Real-time scrape progress via Socket.IO (server emits `scrapeProgress`)
  useEffect(() => {
    let socket;
    const socketOrigin = (() => {
      try {
        const u = new URL(API_BASE_URL);
        return u.origin;
      } catch (e) {
        return window.location.origin;
      }
    })();
    try {
      socket = io(socketOrigin, { path: '/socket.io' });
      socket.on('scrapeProgress', (payload) => {
        if (!payload) return;
        // Only show progress for the current requestId (if any)
        setScrapeProgress((prev) => {
          const activeId = prev?.requestId;
          if (activeId && payload.requestId && payload.requestId !== activeId) return prev;
          return { ...(prev || {}), ...payload };
        });
        setScrapeLogs((prev) => {
          // Keep last ~30 log lines
          let line = `[${payload.phase || 'progress'}] ${payload.message || ''}`.trim();
          if (payload.phase === 'article_done' && payload.metrics) {
            const m = payload.metrics;
            const ms = typeof m.msTotal === 'number' ? `${m.msTotal}ms` : '';
            const goto = typeof m.msGoto === 'number' ? `goto:${m.msGoto}ms` : '';
            const pop = typeof m.msPopups === 'number' ? `popups:${m.msPopups}ms` : '';
            const opened = typeof m.popupsOpened === 'number' ? `opened:${m.popupsOpened}` : '';
            const meta = typeof m.emailsFromMeta === 'number' ? `meta:${m.emailsFromMeta}` : '';
            const added = typeof m.emailsAdded === 'number' ? `added:${m.emailsAdded}` : '';
            const parts = [ms, goto, pop, opened, meta, added].filter(Boolean);
            if (parts.length) line = `${line} • ${parts.join(' ')}`;
          }
          if (!line) return prev;
          const next = [...prev, line];
          return next.length > 30 ? next.slice(next.length - 30) : next;
        });
      });
    } catch (e) {
      // ignore
    }
    return () => {
      try { if (socket) socket.close(); } catch (e) { /* ignore */ }
    };
  }, []);

  const loadAcademicians = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/academicians`);
      if (!response.ok) {
        const text = await response.text();
        console.error('API error', response.status, text);
        throw new Error(`API error ${response.status}`);
      }
      const data = await response.json();
      console.log(data);
      const rows = (data.academicians || []).map((r) => ({
        id: r.id || r.ID,
        ad_soyad: r.name_surname || r.ad_soyad,
        university: r.university || r.SCHOOL || r.university,
        faculty: r.faculty ?? '',
        department: r.department ?? '',
        major: r.major ?? '',
        email: r.email,
      }));
      setAcademicians(rows);
    } catch (error) {
      console.error('Failed to load academicians:', error);
      setAcademicians([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // CRUD Operations
  const handleView = (academician) => {
    setViewItem(academician);
  };

  const handleEdit = async (academician) => {
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/auth/academicians/${academician.id}`);
      if (res.ok) {
        const data = await res.json();
        const r = data.academician || {};
        setEditItem({
          id: r.ID || academician.id,
          ad_soyad: r.name_surname ?? academician.ad_soyad,
          email: r.email ?? academician.email,
          university: r.university ?? academician.university,
          faculty: r.faculty ?? academician.faculty,
          department: r.department ?? academician.department,
          major: r.major ?? academician.major,
          region: r.region ?? academician.region ?? 1,
        });
      } else {
        // fallback to given row
        setEditItem({ ...academician });
      }
    } catch (e) {
      console.error('Edit fetch error:', e);
      setEditItem({ ...academician });
    } finally {
      setSaving(false);
    }
  };

  // Status toggling is handled inside Edit modal

  // AG Grid Column Definitions
  const columnDefs = useMemo(() => [
    
    {
      headerName: 'Name',
      field: 'ad_soyad',
      width: 200,
      sortable: true,
      filter: true
    },
    {
      headerName: 'School',
      field: 'university',
      width: 180,
      sortable: true,
      filter: true
    },
    { headerName: 'Faculty', field: 'faculty', width: 200, sortable: true, filter: true },
    { headerName: 'Department', field: 'department', width: 200, sortable: true, filter: true },
    { headerName: 'Major', field: 'major', width: 200, sortable: true, filter: true },
    {
      headerName: 'Email',
      field: 'email',
      width: 250,
      sortable: true,
      filter: true
    },
    
    {
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="d-flex gap-1">
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => handleView(params.data)}
          >
            <FaEye className="me-1" />
            View
          </Button>
          <Button 
            variant="outline-warning" 
            size="sm"
            onClick={() => handleEdit(params.data)}
          >
            <FaEdit className="me-1" />
            Edit
          </Button>
          {/* Toggle Status removed; status can be changed in Edit modal */}
        </div>
      )
    }
  ], []);

  // AG Grid Default Column Definition
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
    flex: 1
  }), []);

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {/* Küçük global toast bildirimi (sol üst) */}
      {toast && (
        <div 
          className={`app-toast app-toast-${toast.type || 'success'}`}
          role="alert"
          aria-live="polite"
        >
          <strong>✓</strong> {toast.text}
        </div>
      )}

      <Navigation user={user} onLogout={handleLogout} currentPage="journal" />
      <Container fluid className="mt-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <h2 className="page-title">
                             <FaUser className="me-2" />
              Academician List
            </h2>
                         <p className="text-muted">Total {academicians.length} academicians found</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={()=>{ setAddOpen(true); setAddItem({ ad_soyad:'', email:'', university:'', faculty:'', department:'', major:'', region:1, springerUrls:'' }); }}>
              + Add New
            </Button>
          </Col>
        </Row>

        

                 {/* AG Grid */}
         {loading ? (
           <Row className="justify-content-center">
             <Col className="text-center">
               <Spinner animation="border" role="status" variant="primary">
                 <span className="visually-hidden">Loading...</span>
               </Spinner>
               <p className="mt-2">Loading academicians...</p>
             </Col>
           </Row>
         ) : (
           <div className="ag-theme-alpine" style={{ height: '70vh', width: '100%' }}>
                          <AgGridReact
               rowData={academicians}
               columnDefs={columnDefs}
               defaultColDef={defaultColDef}
               pagination={true}
               paginationPageSize={50}
               paginationAutoPageSize={false}
               rowSelection="single"
               animateRows={true}
               
               theme="legacy"
               suppressRowClickSelection={true}
               onGridReady={(params) => {
                 params.api.sizeColumnsToFit();
               }}
             />
           </div>
         )}
      </Container>

      {/* View Modal */}
      <Modal show={!!viewItem} onHide={() => setViewItem(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Academician Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewItem && (
            <div className="table-responsive">
              <table className="table table-sm">
                <tbody>
                  <tr><th className="w-25">Name</th><td>{viewItem.ad_soyad}</td></tr>
                  <tr><th>University</th><td>{viewItem.university}</td></tr>
                  <tr><th>Faculty</th><td>{viewItem.faculty}</td></tr>
                  <tr><th>Department</th><td>{viewItem.department}</td></tr>
                  <tr><th>Major</th><td>{viewItem.major}</td></tr>
                  <tr><th>Email</th><td>{viewItem.email}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewItem(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={!!editItem} onHide={() => setEditItem(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Academician</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editItem && (
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name Surname</Form.Label>
                    <Form.Control value={editItem.ad_soyad || ''} onChange={(e)=>setEditItem({...editItem, ad_soyad:e.target.value})} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control value={editItem.email || ''} onChange={(e)=>setEditItem({...editItem, email:e.target.value})} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>University</Form.Label>
                    <Form.Control value={editItem.university || ''} onChange={(e)=>setEditItem({...editItem, university:e.target.value})} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Faculty</Form.Label>
                    <Form.Control value={editItem.faculty || ''} onChange={(e)=>setEditItem({...editItem, faculty:e.target.value})} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Department</Form.Label>
                    <Form.Control value={editItem.department || ''} onChange={(e)=>setEditItem({...editItem, department:e.target.value})} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Major</Form.Label>
                    <Form.Control value={editItem.major || ''} onChange={(e)=>setEditItem({...editItem, major:e.target.value})} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Region</Form.Label>
                    <Form.Select
                      value={String(editItem.region ?? 1)}
                      onChange={(e)=>setEditItem({...editItem, region: e.target.value === '2' ? 2 : 1})}
                    >
                      <option value="1">Local</option>
                      <option value="2">Global</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
              </Row>
            </Form>
          )}
          {saveMsg && (
            <div className={`alert alert-${saveMsg.type} mt-3 mb-0`}>{saveMsg.text}</div>
          )}
          {saving && scrapeProgress?.requestId && (
            <div className="mt-3">
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <div style={{ fontSize: 13 }}>
                  <div><strong>{scrapeProgress.phase || 'working'}</strong> {scrapeProgress.message || ''}</div>
                  {(typeof scrapeProgress.current === 'number' && typeof scrapeProgress.total === 'number') && (
                    <div className="text-muted">
                      Progress: {scrapeProgress.current}/{scrapeProgress.total}
                      {typeof scrapeProgress.etaSec === 'number' && scrapeProgress.etaSec > 0
                        ? ` • ETA ~${Math.round(scrapeProgress.etaSec / 60)} min`
                        : ''}
                    </div>
                  )}
                </div>
              </div>
              {scrapeLogs.length > 0 && (
                <div className="mt-2 p-2 border rounded" style={{ maxHeight: 160, overflow: 'auto', background: '#0b1220', color: '#d7e3ff', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}>
                  {scrapeLogs.map((l, i) => (
                    <div key={`${i}-${l}`}>{l}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setEditItem(null)}>Cancel</Button>
          <Button variant="primary" disabled={saving || !editItem} onClick={async()=>{
            try{
              setSaving(true);
              setSaveMsg(null);
              const resp = await fetch(`${API_BASE_URL}/auth/academicians/${editItem.id}`, {
                method:'PUT', headers:{'Content-Type':'application/json'},
                body: JSON.stringify({
                  name_surname: editItem.ad_soyad,
                  email: editItem.email,
                  university: editItem.university,
                  faculty: editItem.faculty,
                  department: editItem.department,
                  major: editItem.major,
                  region: editItem.region,
                })
              });
              if(!resp.ok){
                const t = await resp.text().catch(()=> '');
                throw new Error(`Update failed ${resp.status} ${t}`);
              }
              setSaveMsg({ type: 'success', text: 'Saved successfully.' });
              showToast('Academician updated successfully.', 'success');
              setTimeout(()=> setEditItem(null), 600);
              await loadAcademicians();
            }catch(err){console.error(err); setSaveMsg({ type: 'danger', text: err.message || 'Save failed' });}
            finally{setSaving(false);}
          }}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Add New Modal */}
      <Modal show={addOpen} onHide={()=>setAddOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Academician</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name Surname</Form.Label>
                  <Form.Control value={addItem.ad_soyad} onChange={(e)=>setAddItem({...addItem, ad_soyad:e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control value={addItem.email} onChange={(e)=>setAddItem({...addItem, email:e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>University</Form.Label>
                  <Form.Control value={addItem.university} onChange={(e)=>setAddItem({...addItem, university:e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Faculty</Form.Label>
                  <Form.Control value={addItem.faculty} onChange={(e)=>setAddItem({...addItem, faculty:e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Department</Form.Label>
                  <Form.Control value={addItem.department} onChange={(e)=>setAddItem({...addItem, department:e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Major</Form.Label>
                  <Form.Control value={addItem.major} onChange={(e)=>setAddItem({...addItem, major:e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Region</Form.Label>
                  <Form.Select
                    value={String(addItem.region)}
                    onChange={(e)=>setAddItem({...addItem, region: e.target.value === '2' ? 2 : 1})}
                  >
                    <option value="1">Local</option>
                    <option value="2">Global</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Springer Article URLs (One URL per line)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder={"e.g.\nhttps://link.springer.com/article/10.1007/s13369-023-07904-8\nhttps://link.springer.com/article/10.1007/s13369-023-07905-7\nhttps://link.springer.com/article/10.1007/s13369-023-07954-y"}
                    value={addItem.springerUrls || ''}
                    onChange={(e)=>setAddItem({...addItem, springerUrls:e.target.value})}
                  />
                  <Form.Text>Scraped emails will be automatically added to the database.</Form.Text>
                </Form.Group>
              </Col>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Springer Journal List URL (Playwright — tüm sayılar)</Form.Label>
              <Form.Control
                placeholder="https://link.springer.com/journal/10462/articles"
                value={addItem.springerStartListUrl || ''}
                onChange={(e)=>setAddItem({...addItem, springerStartListUrl:e.target.value})}
              />
              <Form.Text>Bu seçenek tüm sayılardaki makaleleri gezip yazar e-postalarını toplar.</Form.Text>
            </Form.Group>
          </Col>
            </Row>
          </Form>
          {saveMsg && (
            <div className={`alert alert-${saveMsg.type} mt-3 mb-0`}>{saveMsg.text}</div>
          )}
          {saving && scrapeProgress?.requestId && (
            <div className="mt-3">
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <div style={{ fontSize: 13 }}>
                  <div><strong>{scrapeProgress.phase || 'working'}</strong> {scrapeProgress.message || ''}</div>
                  {(typeof scrapeProgress.current === 'number' && typeof scrapeProgress.total === 'number') && (
                    <div className="text-muted">Progress: {scrapeProgress.current}/{scrapeProgress.total}</div>
                  )}
                </div>
              </div>
              {scrapeLogs.length > 0 && (
                <div className="mt-2 p-2 border rounded" style={{ maxHeight: 160, overflow: 'auto', background: '#0b1220', color: '#d7e3ff', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}>
                  {scrapeLogs.map((l, i) => (
                    <div key={`${i}-${l}`}>{l}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" disabled={saving} onClick={async()=>{
            try{
              if (!addItem.springerUrls) return;
              setSaving(true);
              setSaveMsg(null);
              
              const urls = addItem.springerUrls.split(/\r?\n/)
                .map(url => url.trim())
                .filter(url => url && url.includes('springer.com'));
              
              if (urls.length === 0) {
                setSaveMsg({ type: 'warning', text: 'No valid Springer URLs found' });
                return;
              }

              setSaveMsg({ type: 'info', text: `Scraping ${urls.length} Springer articles...` });
              
              const resp = await fetch(`${API_BASE_URL}/auth/springer/scrape-emails`, { 
                method:'POST', 
                headers:{'Content-Type':'application/json'}, 
                body: JSON.stringify({ urlList: urls }) 
              });
              
              if(!resp.ok){ 
                const t = await resp.text().catch(()=> ''); 
                throw new Error(`Springer scraping failed ${resp.status} ${t}`); 
              }
              
              const result = await resp.json();
              
              if (result.success) {
                setSaveMsg({ 
                  type: 'success', 
                  text: `Scraping completed! Found ${result.totalUniqueEmails} unique emails, saved ${result.savedEmails} new academicians.` 
                });
                if (result.savedEmails > 0) {
                  showToast(`Springer scraping: ${result.savedEmails} new academicians added.`, 'success');
                } else {
                  showToast('Springer scraping finished, no new academicians found.', 'info');
                }
                await loadAcademicians();
                setAddItem({
                  ad_soyad:'',
                  email:'',
                  university:'',
                  faculty:'',
                  department:'',
                  major:'',
                  region:1,
                  springerUrls:'',
                  springerStartListUrl:'https://link.springer.com/journal/10462/articles'
                });
                setTimeout(() => setAddOpen(false), 2000);
              } else {
                throw new Error(result.error || 'Scraping failed');
              }
            }catch(err){ 
              console.error(err); 
              setSaveMsg({ type: 'danger', text: err.message || 'Springer scraping failed' }); 
            } finally {
              setSaving(false);
            }
          }}>{saving ? 'Scraping...' : 'Scrape Springer'}</Button>
          <Button
            variant="outline-success"
            disabled={saving}
            onClick={async()=>{
              try{
                if(!addItem.springerStartListUrl){
                  setSaveMsg({ type:'warning', text:'Start list URL gerekli' });
                  return;
                }
                setSaving(true);
                const requestId =
                  (typeof crypto !== 'undefined' && crypto?.randomUUID)
                    ? crypto.randomUUID()
                    : `scrape_${Date.now()}_${Math.random().toString(16).slice(2)}`;
                setScrapeProgress({ requestId, phase: 'queued', message: 'Request queued...' });
                setScrapeLogs([]);
                setSaveMsg({ type:'info', text:'Playwright scraping başlatıldı (tüm sayılar)...' });

                const resp = await fetch(`${API_BASE_URL}/springer/scrape`, {
                  method:'POST',
                  headers:{'Content-Type':'application/json'},
                  body: JSON.stringify({
                    requestId,
                    startListUrl: addItem.springerStartListUrl,
                    outfile: 'Springer-Scrape.csv',
                    concurrency: 3,
                    sleepBetweenRequests: 250,
                    navTimeout: 45000
                  })
                });

                if(!resp.ok){
                  const t = await resp.text().catch(()=> '');
                  throw new Error(`Springer Playwright scraping failed ${resp.status} ${t}`);
                }

                const result = await resp.json();
                if(result.success){
                  setSaveMsg({
                    type:'success',
                    text:`Playwright tamam: ${result.totalRows ?? 'N/A'} satır, dosya: ${result.outputFileName || result.outputFile}`
                  });
                  const added = result.savedToDb ?? 0;
                  if (added > 0) {
                    showToast(`Playwright scraping: ${added} new academicians added.`, 'success');
                  } else {
                    showToast('Playwright scraping finished, no new academicians found.', 'info');
                  }
                  // keep final progress visible
                  setScrapeProgress((p) => ({ ...(p || {}), phase: 'done', message: 'Finished' }));
                } else {
                  throw new Error(result.error || 'Scrape failed');
                }
              }catch(err){
                console.error(err);
                setSaveMsg({ type:'danger', text: err.message || 'Scrape failed' });
                setScrapeProgress((p) => ({ ...(p || {}), phase: 'error', message: err.message || 'Scrape failed' }));
              }finally{
                setSaving(false);
              }
            }}
          >
            Springer (Playwright) Tüm Sayılar
          </Button>
          <Button variant="secondary" onClick={()=>setAddOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={!addItem.ad_soyad || !addItem.email} onClick={async()=>{
            try{
              setSaving(true);
              setSaveMsg(null);
              const resp = await fetch(`${API_BASE_URL}/auth/academicians`, {
                method:'POST', headers:{'Content-Type':'application/json'},
                body: JSON.stringify({
                  name_surname: addItem.ad_soyad,
                  email: addItem.email,
                  university: addItem.university,
                  faculty: addItem.faculty,
                  department: addItem.department,
                  major: addItem.major,
                  region: addItem.region
                })
              });
              if(!resp.ok){
                const t = await resp.text().catch(()=> '');
                throw new Error(`Create failed ${resp.status} ${t}`);
              }
              setSaveMsg({ type: 'success', text: 'Created successfully.' });
              showToast('Academician created successfully.', 'success');
              setAddOpen(false);
              await loadAcademicians();
            }catch(err){console.error(err); setSaveMsg({ type: 'danger', text: err.message || 'Create failed' });}
            finally{setSaving(false);}
          }}>Create</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ListAcademicians;
